import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { errorHandleMiddleware } from '@entities/attachment/lib';
import { TAttachmentOnClient } from '@entities/attachment/model/types';
import { COMMENT_ATTACHMENT_EXTENSIONS } from '@entities/comment';
import { generateId } from '@shared/helpers';

export const useAttachments = () => {
    const { t } = useTranslation();
    const [attachedFiles, setAttachedFiles] = useState<TAttachmentOnClient[]>([]);
    const onAddFiles = useCallback((files: File[]) => {
        const nextFiles = files.map((file) => ({
            id: generateId(true) as number,
            file,
            isLoading: false,
        }));

        setAttachedFiles((prev) => [...prev, ...nextFiles]);
    }, []);

    const handleUploadFiles = errorHandleMiddleware({
        t,
        onAddFiles,
        alreadyAttachedLength: attachedFiles.length || 0,
        maxAttachmentSizeInMb: 5,
        validateExtensions: COMMENT_ATTACHMENT_EXTENSIONS,
        multiple: true,
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { files } = e.target;
            if (!files?.length) return;

            handleUploadFiles(Array.from(files));
            e.target.value = '';
        },
        [handleUploadFiles]
    );

    const handleCancel = useCallback((id: TAttachmentOnClient['id']) => {
        setAttachedFiles((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const clearAttachments = useCallback(() => {
        setAttachedFiles([]);
    }, []);

    return {
        attachedFiles,
        handleFileChange,
        handleCancel,
        clearAttachments,
    };
};
